// AdminData.js — Données complètes SAFI Academia

export const COURSES_DATA = {
  A1: {
    label: 'A1 — Grand débutant',
    categories: {
      'Grammar': {
        image: 'safi12',
        courses: [
          {
            id: 'a1-g1',
            title: 'Week 1 — Interests & Introductions',
            duration: '90 min',
            content: `## Week 1 — Interests & Introductions

**Objectif oral :** Se présenter et poser des questions sur les hobbies.

### Warm-up (10 min)
Questions brise-glace en binôme :
- What is your name?
- Where are you from?
- What do you like to do?

### Vocabulary & Grammar in Action (20 min)
**Mots clés :**
- hobby, interest, favorite, like, love, enjoy
- music, sport, reading, cooking, traveling

**Structure :** I like + verb-ing
- I like **reading**.
- I love **playing** football.
- I enjoy **cooking**.

### Guided Practice (20 min)
**Mini-dialogue :**
> A: Hi! My name is Kofi. What's your name?
> B: Hello Kofi! I'm Fatou. Nice to meet you!
> A: Nice to meet you too! What do you like to do?
> B: I love music and I enjoy cooking. What about you?
> A: I like reading and playing football.

### Free Conversation (30 min)
**Activity — Find Someone Who:**
Walk around and ask your classmates:
- Find someone who likes music → ___
- Find someone who enjoys cooking → ___
- Find someone who loves sports → ___

### Wrap-up (10 min)
**Invitation to Act:** This week, introduce yourself in English to one new person outside of class.`
          },
          {
            id: 'a1-g2',
            title: 'Week 2 — Daily Routine & Work',
            duration: '90 min',
            content: `## Week 2 — Daily Routine & Work

**Objectif oral :** Décrire sa journée et parler de son métier actuel/futur.

### Warm-up (10 min)
- What time do you wake up?
- What do you do first in the morning?

### Vocabulary & Grammar in Action (20 min)
**Routine vocabulary :**
- wake up, get up, have breakfast, go to work/school
- come home, have dinner, go to bed

**Jobs vocabulary :**
- teacher, doctor, engineer, student, farmer, trader

**Structure :** I + present simple
- I **wake up** at 6am.
- I **work** at a school.

### Guided Practice (20 min)
**Pattern drill :**
> Every morning, I wake up at ___ . I have breakfast and then I ___ .
> I work/study as a ___ . I usually finish at ___ .

### Free Conversation (30 min)
**Role-play — Interview:**
Student A = journalist, Student B = professional.

### Wrap-up (10 min)
**Invitation to Act:** Tell a family member your daily routine in English.`
          },
          {
            id: 'a1-g3',
            title: 'Week 3 — Invitations & Leisure',
            duration: '90 min',
            content: `## Week 3 — Invitations & Leisure

**Objectif oral :** Accepter ou refuser poliment une invitation.

### Key Expressions
**Inviting :**
- Would you like to...?
- Are you free on Saturday?

**Accepting :**
- Yes, I'd love to!
- That sounds great!

**Refusing politely :**
- I'm sorry, I can't. I have to work.
- Maybe another time?

### Mini-dialogue
> A: Are you free on Saturday? Would you like to go to the market?
> B: I'd love to! What time?
> A: How about 10am?
> B: Perfect! See you then.

### Invitation to Act
Invite someone in English this week — by phone, message or in person.`
          },
          {
            id: 'a1-g4',
            title: 'Week 4 — Family & Descriptions',
            duration: '90 min',
            content: `## Week 4 — Family & Descriptions

**Objectif oral :** Décrire physiquement et moralement ses proches.

### Vocabulary — Family
| Anglais | Français |
|---------|----------|
| mother / mom | mère |
| father / dad | père |
| sister | sœur |
| brother | frère |
| grandmother | grand-mère |
| uncle | oncle |
| aunt | tante |

### Describing people
**Physical:** tall, short, thin, big, young, old
**Personality:** kind, funny, serious, hardworking, shy, friendly

### Structure
- My mother **is** tall and kind.
- My brother **has** short hair.
- He **is** very funny.

### Invitation to Act
Show a photo of your family and describe them in English.`
          },
          {
            id: 'a1-g5',
            title: 'Week 5 — Food & Cooking',
            duration: '90 min',
            content: `## Week 5 — Food & Cooking

**Objectif oral :** Commander au restaurant et expliquer une recette simple.

### At the Restaurant
> Waiter: Good evening! Are you ready to order?
> Customer: Yes, I'd like the grilled fish, please.
> Waiter: And to drink?
> Customer: Water, please. How much is it?
> Waiter: It's 3,500 FCFA.

### Talking about food preferences
- I **like** rice and fish.
- I **don't like** spicy food.
- My favorite food is **thiéboudienne**.

### Invitation to Act
Order something in English at a shop or café this week.`
          },
          {
            id: 'a1-g6',
            title: 'Week 6 — Shopping & Prices',
            duration: '90 min',
            content: `## Week 6 — Shopping & Prices

**Objectif oral :** Demander des prix et négocier un achat.

### Key Phrases
- How much is this?
- It's too expensive. Can you give me a discount?
- I'll take it.

### Numbers & Prices
- 500 FCFA → five hundred francs
- 2,000 FCFA → two thousand francs
- 15,000 FCFA → fifteen thousand francs

### Role-play — Market Scene
> Customer: Excuse me, how much is this bag?
> Seller: It's 8,000 francs.
> Customer: That's expensive! Can you do 5,000?
> Seller: OK, 6,500. Final price.
> Customer: Deal! I'll take it.`
          },
          {
            id: 'a1-g7',
            title: 'Week 7 — Health & Body',
            duration: '90 min',
            content: `## Week 7 — Health & Body

**Objectif oral :** Expliquer un problème de santé et donner des conseils.

### Health problems
- I have a **headache**.
- I have a **fever** / a **cold** / a **cough**.
- My **back** hurts.
- I feel **tired** / **dizzy** / **sick**.

### Giving advice
- You should **rest**.
- You should **drink water**.
- You shouldn't **eat spicy food**.

### At the Doctor's
> Doctor: What's the problem?
> Patient: I have a headache and a fever.
> Doctor: You should rest and drink plenty of water.`
          },
          {
            id: 'a1-g8',
            title: 'Week 8 — In the Community',
            duration: '90 min',
            content: `## Week 8 — In the Community

**Objectif oral :** Demander et donner des directions dans une ville.

### Asking for directions
- Excuse me, where is the **bank**?
- How do I get to the **hospital**?

### Giving directions
- Go **straight**.
- Turn **left** / **right**.
- It's **next to** / **opposite** / **between** ___

### Practice dialogue
> A: Excuse me, how do I get to the market?
> B: Go straight, then turn left at the school. The market is on your right.
> A: Thank you very much!`
          },
          {
            id: 'a1-g9',
            title: 'Week 9 — Past Experiences',
            duration: '90 min',
            content: `## Week 9 — Past Experiences

**Objectif oral :** Raconter une anecdote ou un événement passé.

### Past Simple — Regular verbs (+ ED)
- work → **worked** | play → **played** | study → **studied**

### Past Simple — Irregular verbs
| Infinitive | Past |
|------------|------|
| go | went |
| have | had |
| see | saw |
| eat | ate |
| meet | met |

### Talking about the weekend
- Last weekend, I **went** to the market.
- I **met** my friend and we **ate** together.
- It **was** very fun!`
          },
          {
            id: 'a1-g10',
            title: 'Week 10 — Future Plans',
            duration: '90 min',
            content: `## Week 10 — Future Plans

**Objectif oral :** Parler de ses objectifs personnels et professionnels.

### Going to — Plans décidés
- I **am going to** study medicine.
- She **is going to** open a business.

### Will — Décisions spontanées / Prédictions
- I think I **will** be a doctor one day.
- I **will** help you.

### Dreams and ambitions
- My dream is **to become** a teacher.
- In 5 years, I hope **to have** my own company.`
          },
          {
            id: 'a1-g11',
            title: 'Week 11 — Travel & Holidays',
            duration: '90 min',
            content: `## Week 11 — Travel & Holidays

**Objectif oral :** Planifier un voyage et réserver un hôtel.

### At the hotel
> Receptionist: Do you have a reservation?
> Guest: Yes, my name is Diallo. I booked a room for 3 nights.
> Receptionist: Here is your key. Room 205.
> Guest: What time is breakfast?
> Receptionist: From 7 to 10am.

### Planning a trip
- I want to **visit** Paris next year.
- I **plan to** travel by plane.
- The trip **will cost** about 500,000 FCFA.`
          },
          {
            id: 'a1-g12',
            title: 'Week 12 — Final Oral Review',
            duration: '90 min',
            content: `## Week 12 — Final Oral Review

**Objectif :** Soutenance orale finale — "My Progress with English"

### Structure (3-5 minutes)

**1. Introduction**
- Hello everyone, my name is ___ and I am from ___.

**2. What I learned**
- I learned how to introduce myself and talk about my hobbies.
- I can now describe my daily routine and talk about my work.

**3. My favorite lesson**
- My favorite week was Week ___ because ___.

**4. My progress**
- Before this course, I couldn't speak English at all.
- Now, I can have simple conversations.

**5. My future goals**
- I am going to continue studying English.

**Évaluation /20**
- Fluidité : /5 | Vocabulaire : /5 | Grammaire : /5 | Confiance : /5`
          },
        ],
      },
      'Conjugating': {
        image: 'safi13',
        courses: [
          { id: 'a1-c1', title: 'Le verbe To Be', duration: '20 min', content: `## Le Verbe To Be (Être)\n\n### Forme affirmative\n| Pronom | Forme | Contraction |\n|--------|-------|-------------|\n| I | am | I'm |\n| You | are | You're |\n| He/She/It | is | He's/She's/It's |\n| We/They | are | We're/They're |\n\n### Forme négative\n- I **am not** / I'm not\n- She **is not** / She isn't\n\n### Forme interrogative\n- **Am** I late?\n- **Is** she your friend?\n- **Are** they students?` },
          { id: 'a1-c2', title: 'Le présent simple', duration: '30 min', content: `## Le Présent Simple\n\nPour les habitudes, routines et vérités générales.\n\n### Structure\n- I/You/We/They + **verbe de base**\n- He/She/It + **verbe + S**\n\n### Exemples\n- I **work** every day.\n- She **works** at a hospital.\n\n### Forme négative\n- I **don't** work on Sundays.\n- She **doesn't** like coffee.\n\n### Marqueurs de temps\n- every day, always, usually, often, sometimes, never` },
          { id: 'a1-c3', title: 'Have / Has', duration: '20 min', content: `## Have / Has\n\n- I/You/We/They → **have**\n- He/She/It → **has**\n\n### Exemples\n- I **have** a car.\n- She **has** two sisters.\n\n### Forme négative\n- I **don't have** time.\n- He **doesn't have** a phone.\n\n### Forme interrogative\n- **Do** you **have** a pen?\n- **Does** she **have** a brother?` },
        ],
      },
      'Reading & Speaking': {
        image: 'safi14',
        courses: [
          {
            id: 'a1-rs1',
            title: 'Week 1 — A New Beginning',
            duration: '60 min',
            audio: true,
            content: `## Week 1 — A New Beginning

*A short story about starting something new.*

My name is Aminata. I am twenty-two years old and I live in Dakar, Senegal. Every morning, I wake up early and watch the sunrise over the Atlantic Ocean. The sky turns orange and pink, and the city slowly wakes up around me.

This year, I decided to do something important. I decided to learn English. Many people in my neighborhood speak French and Wolof, but English is the language of opportunity. I want to travel, find a good job, and communicate with people from all over the world.

On my first day of English class, I was nervous. My hands were shaking when I walked into the classroom. There were fifteen other students — some young, some older. The teacher, Mr. Johnson, smiled and said, "Welcome everyone. Today is a new beginning."

Those words stayed with me. A new beginning. I repeated them softly to myself. I wrote them in my notebook and drew a small star next to them. Every big journey starts with one small step.

In class, we learned to say our names, where we come from, and what we like to do. I said, "My name is Aminata. I am from Dakar. I like music and cooking." It was simple, but it felt wonderful to speak in a new language.

After class, I walked home through the busy streets of Dakar. I passed the market, the mosque, and the old school where I studied as a child. I thought about all the things I wanted to achieve. I thought about the future.

That evening, I opened my notebook and wrote one sentence in English: "Today is the first day of my new life." I smiled, closed the book, and fell asleep with hope in my heart.`
          },
          {
            id: 'a1-rs2',
            title: 'Week 2 — The Market Day',
            duration: '60 min',
            audio: true,
            content: `## Week 2 — The Market Day

*A story about a busy day at the local market.*

Every Saturday morning, Oumar goes to the big market in his city. He wakes up at six o'clock, drinks a cup of sweet tea, and puts on his best boubou. The market is only fifteen minutes away on foot, but Oumar always leaves early because the best vegetables sell quickly.

The market is loud and full of life. There are sellers everywhere — men and women shouting prices, children running between the stalls, and the wonderful smells of spices, fresh fish, and roasted peanuts filling the air. Oumar loves this place. It reminds him of his childhood when his grandmother used to bring him here every week.

Today, Oumar has a list. He needs tomatoes, onions, okra, fresh fish, and some mangoes. He also wants to buy a small gift for his sister, who is celebrating her birthday next week. He walks slowly through the market, stopping at each stall to look at the goods.

At the fish stall, he meets an old friend named Ibrahima. They shake hands warmly and talk for a few minutes. Ibrahima tells him that his brother just returned from France and that he speaks English very well now. "English opens many doors," Ibrahima says with a big smile.

Oumar thinks about this as he continues shopping. He knows that in today's world, speaking English is very important. He has been studying English for three weeks now, and he is already learning many new words. He practices every evening after dinner, repeating phrases quietly to himself.

At the mango stall, he tries something new. He speaks to the seller in English. "How much is this?" he asks, pointing at the biggest mango. The seller laughs with surprise and answers in Wolof, but Oumar is proud of himself. He tried. That is what matters.

He returns home with his bags full and his heart happy. He puts the mangoes on the table and picks up his English notebook. Today was a good day.`
          },
          {
            id: 'a1-rs3',
            title: 'Week 3 — The Job Interview',
            duration: '60 min',
            audio: true,
            content: `## Week 3 — The Job Interview

*A dialogue about preparing for and going to a job interview.*

Mariama is twenty-six years old. She studied accounting at university and is now looking for her first professional job. She found an advertisement for a position at an international company in Abidjan. The interview will be in English.

For two weeks before the interview, Mariama practices every day. She reads articles in English, watches videos, and repeats common interview questions in front of her mirror. Her older brother helps her practice. He pretends to be the interviewer while she answers his questions.

On the morning of the interview, she wakes up early and feels nervous. She puts on a professional outfit — a dark blue jacket and smart trousers. She drinks a glass of water and takes a deep breath.

The interview begins at ten o'clock. There are two interviewers: a woman named Mrs. Chen and a man named Mr. Adeyemi.

Mrs. Chen smiles and says: "Good morning, Mariama. Please take a seat. Tell us about yourself."

Mariama takes a breath and begins: "Good morning. My name is Mariama Coulibaly. I am from Abidjan. I studied accounting at the University of Abidjan, where I graduated with honors. I am hardworking, organized, and I love solving problems."

Mr. Adeyemi asks: "Why do you want to work for our company?"

Mariama answers confidently: "I want to work here because your company has an excellent reputation. I believe I can contribute to the team and grow professionally."

After forty-five minutes, the interview ends. Mrs. Chen shakes her hand and says: "Thank you, Mariama. We will contact you within one week."

Three days later, Mariama receives an email. She got the job. She reads the email three times, then calls her mother to share the wonderful news.`
          },
          {
            id: 'a1-rs4',
            title: 'Week 4 — The Wonders of Senegal',
            duration: '60 min',
            audio: true,
            content: `## Week 4 — The Wonders of Senegal

*An informative text about the beauty and culture of Senegal.*

Senegal is a country located on the western coast of Africa. It is bordered by Mauritania to the north, Mali to the east, Guinea-Bissau and Guinea to the south, and the Atlantic Ocean to the west. The country covers an area of about 197,000 square kilometers and has a population of approximately 17 million people.

The capital city of Senegal is Dakar. It is a vibrant and modern city built on a peninsula at the westernmost point of mainland Africa. Dakar is known for its beautiful beaches, colorful markets, and lively music scene. The city never sleeps — there is always something happening on its busy streets.

Senegal is famous for its culture and traditions. The people of Senegal are known for their warmth and hospitality. There is even a special word for this in Wolof, the most widely spoken local language: "Teranga." It means welcome, generosity, and the spirit of sharing with others.

The country has many beautiful natural places. The Pink Lake, known as Lac Rose, is one of the most unusual lakes in the world. The water appears pink due to a special type of algae that lives in it. Tourists come from all over the world to see it and take photographs. The Casamance region in the south is another wonder — it is covered with lush green forests, rivers, and rice fields.

Senegal also has a rich history. Gorée Island, just off the coast of Dakar, was once an important site during the time of the slave trade. Today, it is a UNESCO World Heritage Site and a place of memory and education for people from all over the world.

The most popular sport in Senegal is wrestling, known as "Lutte Sénégalaise." It is more than just a sport — it is a cultural event with music, dance, and spiritual rituals. Football is also very popular, and the Senegalese national team, known as the Lions of Teranga, has represented the country proudly on the international stage.

Senegal is a country of beauty, culture, and resilience. Its people face many challenges, but they do so with pride, dignity, and an unbreakable spirit.`
          },
          {
            id: 'a1-rs5',
            title: 'Week 5 — Two Friends, One Dream',
            duration: '60 min',
            audio: true,
            content: `## Week 5 — Two Friends, One Dream

*A story about friendship and working towards a shared goal.*

Kofi and Seun have been best friends since they were seven years old. They grew up in the same neighborhood, went to the same school, and shared the same dream: to one day start a business together. Now they are both twenty-five, and that dream is finally becoming real.

Kofi is quiet and thoughtful. He loves reading business books and planning every detail carefully. Seun is energetic and creative. He is always full of ideas and loves talking to people. Together, they make a perfect team.

Their business idea is simple but powerful. They want to create a small company that teaches digital skills to young people in their community. They know that many young Africans have the intelligence and the desire to succeed, but they do not always have access to the right tools and knowledge.

For months, they saved money, attended workshops, and talked to mentors. They also studied English together because they knew that most international business is done in English. They practiced every evening, reading articles, watching tutorials, and writing emails.

One morning, they received good news. A local organization agreed to give them a small grant to start their project. They jumped and shouted with joy in the middle of the street, not caring who was watching.

The first day of their program, twelve young people came to learn. Kofi taught basic computer skills while Seun showed them how to use social media for business. At the end of the day, one of the students, a seventeen-year-old girl named Adaeze, said: "This is the first time someone believed in me."

Those words meant everything to them. That evening, Kofi wrote in his journal: "We started today. We will not stop."

Their journey has just begun, but they are not afraid. They have a plan, they have each other, and they have a dream.`
          },
          {
            id: 'a1-rs6',
            title: 'Week 6 — Climate Change and Africa',
            duration: '60 min',
            audio: true,
            content: `## Week 6 — Climate Change and Africa

*An informative text about how climate change affects the African continent.*

Climate change is one of the most serious challenges facing our world today. It refers to long-term shifts in temperatures and weather patterns, mainly caused by human activities such as burning fossil fuels, cutting down forests, and industrial pollution. While climate change is a global problem, its effects are felt most strongly in Africa.

Africa is responsible for less than four percent of the world's total greenhouse gas emissions. However, it is one of the continents most affected by the consequences of climate change. This is deeply unfair, and it is a situation that scientists, leaders, and young people around the world are working hard to change.

In West Africa, rainfall patterns have changed significantly. Some areas that used to receive regular rain now face long periods of drought. Farmers who depend on rain to grow their crops are struggling. When harvests fail, families go hungry, and communities face serious economic difficulties. In the Sahel region, the desert is slowly expanding southward, a process called desertification. This is destroying land that was once used for farming and grazing animals.

Along the coast, rising sea levels are threatening cities and villages. In countries like Senegal, the ocean is slowly eating away at the coastline. Some fishing communities that have existed for hundreds of years are now at risk of disappearing underwater within the next few decades.

However, Africa is not simply a victim. Across the continent, young people, scientists, and community leaders are finding creative solutions. Farmers are using new techniques to conserve water. Engineers are building solar energy systems to provide clean electricity. Youth activists are speaking at international conferences, demanding justice and action.

The story of climate change in Africa is not just a story of suffering. It is also a story of strength, innovation, and the determination of a continent that refuses to give up.`
          },
          {
            id: 'a1-rs7',
            title: 'Week 7 — A Letter Home',
            duration: '60 min',
            audio: true,
            content: `## Week 7 — A Letter Home

*A personal letter written by a student studying abroad.*

Dear Mama,

I hope this letter finds you well. I miss you and Papa every single day, but I am doing well here in London. Life in England is very different from home, but I am learning and growing every day.

The city is enormous. There are people from every country in the world here, speaking hundreds of different languages. Sometimes when I walk down the street, I hear French, Arabic, Hindi, and Yoruba all within the same minute. It reminds me that the world is much bigger than our small neighborhood, and that is both exciting and a little scary.

My English classes are going very well. When I first arrived, I was afraid to speak because I did not want to make mistakes. My teacher, Mrs. Patterson, told me something I will never forget. She said: "The only way to learn a language is to use it, even when it is difficult. Mistakes are not failures. They are steps forward." After that, I decided to be brave.

Now I speak English everywhere I go — at the supermarket, on the bus, with my neighbors. My accent is still strong, and sometimes people ask me to repeat myself, but I do not feel ashamed anymore. I feel proud.

The food here is very different from home. I really miss your thiéboudienne, Mama. I tried to make it last week, but it did not taste the same. Maybe you can teach me your secret recipe when I come home.

The weather is also very different. It rains almost every day, and the sky is often grey. I have bought a thick winter coat and two pairs of warm boots. When I wear them, I think of the warm Dakar sun and I smile.

I will return home in December. I am counting the days. Please take care of yourself and give my love to everyone.

Your daughter,
Ndéye`
          },
          {
            id: 'a1-rs8',
            title: 'Week 8 — The Power of Education',
            duration: '60 min',
            audio: true,
            content: `## Week 8 — The Power of Education

*An informative text about the importance of education in Africa.*

Education is one of the most powerful tools for changing a person's life, a community's future, and a country's destiny. Across Africa, millions of young people go to school every day with one hope in their hearts: to learn, to grow, and to build a better life for themselves and their families.

In many parts of Africa, access to education has improved dramatically over the past few decades. More children are enrolled in primary school than ever before. Governments are building new classrooms, training teachers, and providing books and materials. International organizations are supporting these efforts with funding and technical expertise.

However, serious challenges remain. In rural and remote areas, schools are sometimes very far from where children live. Young girls, in particular, face many obstacles. In some communities, cultural traditions discourage girls from continuing their education after a certain age. Early marriage is still common in some areas, which forces many girls to leave school before they can complete their studies.

Despite these challenges, there are countless inspiring stories of young people who refused to give up. Malala Yousafzai, from Pakistan, was shot by extremists for going to school. She survived and became a global symbol of the right to education. In Africa, young women like Vanessa Nakate from Uganda are using their voices to demand not only environmental justice but also equal access to education and opportunity.

Teachers also play a crucial role. In many communities, a good teacher does not just teach a subject — they change lives. They inspire their students to dream bigger, to work harder, and to believe that they are capable of achieving great things.

Learning English is part of this educational journey. English connects students to a global community of knowledge, opportunity, and dialogue. It opens doors to universities, careers, and friendships across borders.

Education is not a privilege. It is a right. And when young people are educated, the whole world benefits.`
          },
          {
            id: 'a1-rs9',
            title: 'Week 9 — The Old Fisherman',
            duration: '60 min',
            audio: true,
            content: `## Week 9 — The Old Fisherman

*A story about wisdom, tradition, and the sea.*

Old Modu has been a fisherman for fifty years. Every morning before sunrise, he walks down to the beach, pushes his wooden boat into the water, and rows out to sea. His body is old and his hands are rough, but his eyes are still sharp and his heart is still strong.

Modu was born in a small fishing village on the coast of Senegal. His father was a fisherman, and his grandfather before him. The sea is in his blood. He knows its moods, its currents, and its secrets better than he knows his own house.

In the village, Modu is respected by everyone. The young fishermen come to him for advice. They want to know where to find the biggest fish, how to read the weather, and how to stay safe when the sea becomes angry. Modu always answers patiently, using simple words and stories that everyone can understand.

One afternoon, a young man named Cheikh came to visit Modu. Cheikh was twenty years old and full of ambition. He had just returned from the city, where he had been studying. He sat down next to Modu under the big mango tree and said: "Old man, I want to learn your wisdom. But I also want to move forward. How can I honor the past without being trapped by it?"

Modu was quiet for a long time. He looked at the sea. Then he said: "Look at the ocean, my son. The water is always moving. It never stays in one place. But it always remembers where it came from. It always returns to the shore."

Cheikh thought about these words for many days. He understood that he could embrace the modern world — he could study, travel, and learn new languages — while still carrying his culture and his roots inside him. He did not have to choose between the old and the new. He could hold both.

Modu smiled when Cheikh came back to tell him this. "You are ready," the old fisherman said. And for the first time in a long time, Cheikh felt completely at peace.`
          },
          {
            id: 'a1-rs10',
            title: 'Week 10 — Technology in Africa',
            duration: '60 min',
            audio: true,
            content: `## Week 10 — Technology in Africa

*An informative text about the digital revolution across the African continent.*

Africa is experiencing a technological revolution. In cities and towns across the continent, young entrepreneurs, engineers, and innovators are using technology to solve local problems and build global businesses. This movement is sometimes called "the African tech boom," and it is changing the way the world sees the continent.

Mobile technology has been a game-changer in Africa. Many people in Africa did not have access to traditional banking services, but with mobile money platforms like M-Pesa in Kenya and Wave in Senegal, millions of people can now send money, pay bills, and save funds using only their smartphones. This has transformed the lives of farmers, small business owners, and families living far from city centers.

Lagos, Nairobi, Cairo, and Cape Town are now recognized as major tech hubs. Lagos, in Nigeria, has been called "Africa's Silicon Valley." Hundreds of startups are based there, working on everything from healthcare technology to agricultural solutions to e-commerce platforms. Investors from around the world are paying attention to what is happening in Africa, and billions of dollars have been invested in African tech companies in recent years.

Education technology is also growing fast. Online learning platforms are giving students in remote areas access to quality education that they would never have had before. Young Africans can now study computer programming, business, engineering, and languages from their phones or computers, often for free.

Of course, challenges remain. Access to reliable electricity and internet connectivity is still unequal across the continent. Many rural areas still struggle with basic infrastructure. Governments and private companies must work together to close the digital divide.

But the progress is real, and the energy is extraordinary. Africa has the youngest population in the world — and that young generation is creative, ambitious, and ready to build the future.`
          },
          {
            id: 'a1-rs11',
            title: 'Week 11 — The Doctor\'s Choice',
            duration: '60 min',
            audio: true,
            content: `## Week 11 — The Doctor's Choice

*A story about sacrifice, purpose, and the meaning of success.*

Dr. Fatimata Diallo had a choice to make. She was thirty-four years old, one of the most talented young doctors in her country, and she had just received two offers. The first was from a large hospital in Paris, France, offering an excellent salary, modern equipment, and a comfortable life in one of the world's most beautiful cities. The second was from a small rural clinic in northern Senegal, where patients sometimes walked for hours to receive medical care.

Her colleagues at the hospital told her the answer was obvious. "Take the Paris offer," they said. "You have worked too hard to stay here. You deserve better." But Fatimata was not sure what "better" meant anymore.

She thought about the day she decided to become a doctor. She was twelve years old when her younger brother became very sick with malaria. The nearest clinic was far away, and her family had no money for transport. By the time they arrived, her brother was very weak. Fortunately, a kind doctor saved his life. That day, Fatimata decided she would spend her life helping people who did not have easy access to medical care.

Had she forgotten that promise?

She spent a week thinking, praying, and talking to the people she trusted most — her mother, her mentor, and her oldest friend. In the end, the answer came not from logic, but from her heart.

She chose the rural clinic.

When she arrived, there was no welcome party, no modern equipment, and no comfortable apartment. But on her first day, she treated forty-seven patients. At the end of the day, an elderly woman took her hands and said in Pulaar: "God sent you to us."

Fatimata went to her small room that evening, sat on the edge of her bed, and cried — not from sadness, but from the deepest sense of purpose she had ever felt.

She was exactly where she was supposed to be.`
          },
          {
            id: 'a1-rs12',
            title: 'Week 12 — The World in One Classroom',
            duration: '60 min',
            audio: true,
            content: `## Week 12 — The World in One Classroom

*A reflection on language, connection, and the power of learning.*

On the last day of the English course, the teacher asked each student to stand up and share one thing they had learned — not just about English, but about themselves.

There were twelve students in the class. They came from six different countries: Senegal, Gabon, Cameroon, Ivory Coast, Guinea, and Mali. They were of different ages, different backgrounds, and different dreams. But for twelve weeks, they had shared something powerful: the experience of learning together.

The first to speak was Ibrahim, a forty-two-year-old businessman from Dakar. He stood up slowly and said: "Before this class, I was afraid of English. I thought it was too difficult for me. But I learned that fear is just a feeling — it is not the truth. I can learn. It is not too late for me."

Next was Aissatou, a nineteen-year-old student from Conakry. She spoke with bright eyes: "I learned that English is not just a language. It is a bridge. It connects me to people I could never have spoken to before. It makes the world smaller and bigger at the same time."

Then came Moussa, a quiet young farmer from Mali. He had struggled the most in class, but he had never given up. He said simply: "I learned that the most important thing is not to be perfect. The most important thing is to try."

One by one, each student shared their story. Each one was different, but all of them contained the same thread: courage, growth, and the joy of discovering something new about themselves and the world.

The teacher, who had been smiling the whole time, spoke last. She said: "You came here to learn English. But what you actually learned is much more than words and grammar. You learned to be brave. You learned to listen. You learned that every person in this room has a story worth hearing."

As the students left the classroom for the last time, they exchanged phone numbers, laughed, and made promises to stay in touch. A classroom had become a community. And twelve strangers had become something that would last much longer than the course: friends.`
          },
        ],
      },
    },
  },
  A2: {
    label: 'A2 — Élémentaire',
    categories: {
      'Grammar': {
        image: 'safi12',
        courses: [
          { id: 'a2-g1', title: 'Les comparatifs et superlatifs', duration: '30 min', content: `## Comparatifs et Superlatifs\n\n### Comparatif\n- Adjectif court + **er than** : tall → **taller than**\n- More + adjectif long : **more interesting than**\n\n### Superlatif\n- The + adjectif court + **est** : **the tallest**\n- The most + adjectif long : **the most interesting**\n\n### Exemples\n- Dakar is **bigger than** Saint-Louis.\n- She is **the best** student in the class.` },
          { id: 'a2-g2', title: 'Les prépositions', duration: '25 min', content: `## Les Prépositions\n\n### Temps\n- **at** → heure : at 8am\n- **on** → jour : on Monday\n- **in** → mois/année : in June\n\n### Lieu\n- **in** → dans : in the bag\n- **on** → sur : on the table\n- **at** → à : at school` },
        ],
      },
      'Conjugating': {
        image: 'safi13',
        courses: [
          { id: 'a2-c1', title: 'Présent simple vs continu', duration: '30 min', content: `## Présent Simple vs Continu\n\n### Présent Simple → habitudes\n- She **works** every day.\n\n### Présent Continu → maintenant\n- She **is working** right now.\n\n### Signal words\n- **Simple** : always, every day, never\n- **Continu** : now, at the moment` },
          { id: 'a2-c2', title: 'Le passé simple', duration: '35 min', content: `## Le Passé Simple\n\n### Réguliers → +ED\n- work → **worked** | play → **played**\n\n### Irréguliers\n| Infinitif | Passé |\n|-----------|-------|\n| go | went |\n| have | had |\n| see | saw |\n| eat | ate |\n\n### Négatif\n- I **didn't** go yesterday.\n\n### Interrogatif\n- **Did** you see the match?` },
        ],
      },
      'Reading & Speaking': {
        image: 'safi14',
        courses: [
          { id: 'a2-rs1', title: 'Email professionnel', duration: '25 min', audio: true, content: `## Email Professionnel\n\n### Structure\n1. **Objet** : clair et précis\n2. **Salutation** : Dear Mr./Ms. [Nom],\n3. **Corps** : raison + détails\n4. **Conclusion** : Best regards,\n\n### Exemple\n> **Subject:** English Course Registration\n> Dear Ms. Diallo,\n> I am writing to register for the English A1 course.\n> Best regards,\n> Kofi Mensah` },
          { id: 'a2-rs2', title: 'Parler de sa journée', duration: '25 min', audio: true, content: `## Parler de sa Journée\n\n### Vocabulaire\n| Anglais | Français |\n|---------|----------|\n| wake up | se réveiller |\n| get dressed | s'habiller |\n| have breakfast | petit-déjeuner |\n| go to work | aller au travail |\n| come home | rentrer |\n| go to bed | se coucher |` },
        ],
      },
    },
  },
};

export const QUIZ_DATA = {
  courses: {
    // Grammar A1 (12 semaines)
    'a1-g1': { title: 'Quiz — Week 1: Introductions', questions: [
      { q: '"Nice to meet you" is used when:', opts: ['saying goodbye','meeting someone for the first time','asking for help','ordering food'], a: 1 },
      { q: 'I like ___. Which is correct?', opts: ['dance','dancing','to dancing','danced'], a: 1 },
      { q: 'What is a "hobby"?', opts: ['A job','A type of food','An activity you enjoy in free time','A place to live'], a: 2 },
      { q: '"What\'s your name?" — correct answer:', opts: ["I'm fine.","My name is Kofi.","I am from Dakar.","Nice to meet you."], a: 1 },
      { q: 'I ___ music. (love)', opts: ['loves','love','loving','loved'], a: 1 },
    ]},
    'a1-g2': { title: 'Quiz — Week 2: Daily Routine', questions: [
      { q: '"Wake up" means:', opts: ['go to sleep','start sleeping','stop sleeping','dream'], a: 2 },
      { q: 'I ___ to work by bus every day.', opts: ['go','goes','going','went'], a: 0 },
      { q: '"Every morning" tells us the action is:', opts: ['in the past','happening now','a habit/routine','in the future'], a: 2 },
      { q: 'She ___ at 6pm every evening. (come home)', opts: ['come home','comes home','coming home','came home'], a: 1 },
      { q: 'Which word describes a job?', opts: ['breakfast','teacher','morning','always'], a: 1 },
    ]},
    'a1-g3': { title: 'Quiz — Week 3: Invitations', questions: [
      { q: '"Would you like to come?" is:', opts: ['a refusal','an invitation','a direction','a complaint'], a: 1 },
      { q: 'How to refuse politely:', opts: ["Yes, I'd love to!","No problem!","I'm sorry, I can't.","Of course!"], a: 2 },
      { q: '"That sounds great!" means:', opts: ["I don't want to.","I'm not sure.","I accept with enthusiasm.","I need to think."], a: 2 },
      { q: '"Are you ___ on Saturday?"', opts: ['busy','free','happy','ready'], a: 1 },
      { q: '"Maybe another time" is:', opts: ['an acceptance','a polite refusal','a question','a direction'], a: 1 },
    ]},
    'a1-g4': { title: 'Quiz — Week 4: Family', questions: [
      { q: 'Your mother\'s brother is your:', opts: ['cousin','uncle','grandfather','nephew'], a: 1 },
      { q: '"Siblings" means:', opts: ['parents','brothers and sisters','cousins','friends'], a: 1 },
      { q: '"Kind" describes:', opts: ['height','age','personality','job'], a: 2 },
      { q: 'My sister ___ long hair.', opts: ['have','has','is','are'], a: 1 },
      { q: '"Friendly" means:', opts: ['serious','shy','easy to talk to','hardworking'], a: 2 },
    ]},
    'a1-g5': { title: 'Quiz — Week 5: Food', questions: [
      { q: '"I\'d like the grilled fish" is used:', opts: ['at the market','at the restaurant','at the hospital','at school'], a: 1 },
      { q: '"How much is it?" means:', opts: ['Where is it?','What is it?','What is the price?','Do you have it?'], a: 2 },
      { q: 'I ___ spicy food. (not like)', opts: ["don't like","doesn't like","am not like","isn't like"], a: 0 },
      { q: '"To order" at a restaurant means:', opts: ['to cook','to ask for food you want','to pay','to leave'], a: 1 },
      { q: 'Which is a meal?', opts: ['breakfast','teacher','friendly','tall'], a: 0 },
    ]},
    'a1-g6': { title: 'Quiz — Week 6: Shopping', questions: [
      { q: '"How much does it cost?" means:', opts: ["Where can I find it?","What color is it?","What is the price?","Do you have my size?"], a: 2 },
      { q: '"That\'s too expensive" means:', opts: ["The price is OK.","The price is very low.","The price is too high.","I want to buy it."], a: 2 },
      { q: '"Deal!" in a negotiation means:', opts: ["I refuse.","I agree to the price.","I need to think.","It's too expensive."], a: 1 },
      { q: '15,000 FCFA in words:', opts: ['fifteen hundred','one thousand five hundred','fifteen thousand','one hundred fifty'], a: 2 },
      { q: '"I\'ll take it" means:', opts: ["I don't want it.","I want to buy it.","It's too expensive.","Let me think."], a: 1 },
    ]},
    'a1-g7': { title: 'Quiz — Week 7: Health', questions: [
      { q: '"I have a headache" means:', opts: ['My stomach hurts.','My head hurts.','I have a fever.','I am tired.'], a: 1 },
      { q: 'Good advice when someone is sick:', opts: ["Eat spicy food.","Rest and drink water.","Don't sleep.","Work more."], a: 1 },
      { q: '"Dizzy" means:', opts: ['very tired','feeling like the room is spinning','having a cough','feeling cold'], a: 1 },
      { q: '"You shouldn\'t" is used to:', opts: ['advise to do something','advise NOT to do something','ask a question','make a plan'], a: 1 },
      { q: 'Which is a body part?', opts: ['fever','headache','shoulder','cough'], a: 2 },
    ]},
    'a1-g8': { title: 'Quiz — Week 8: Directions', questions: [
      { q: '"Go straight" means:', opts: ['turn left','turn right','continue without turning','stop here'], a: 2 },
      { q: '"It\'s opposite the school" means:', opts: ["next to the school","behind the school","facing the school","inside the school"], a: 2 },
      { q: '"Excuse me, where is the pharmacy?" — you are:', opts: ['shopping','asking for directions','ordering food','greeting someone'], a: 1 },
      { q: '"Turn left at the bank" — you go:', opts: ['right at the bank','straight at the bank','left at the bank','back at the bank'], a: 2 },
      { q: 'Which is a place in the community?', opts: ['headache','breakfast','pharmacy','friendly'], a: 2 },
    ]},
    'a1-g9': { title: 'Quiz — Week 9: Past', questions: [
      { q: 'Past of "go":', opts: ['goed','gone','went','goes'], a: 2 },
      { q: 'Past of "eat":', opts: ['eated','eaten','ate','eats'], a: 2 },
      { q: '"Last week" signals which tense?', opts: ['Present simple','Present continuous','Past simple','Future'], a: 2 },
      { q: 'I ___ a great film yesterday. (watch)', opts: ['watch','watches','watching','watched'], a: 3 },
      { q: 'Negative past: I ___ to the market.', opts: ["don't go","doesn't go","didn't go","wasn't go"], a: 2 },
    ]},
    'a1-g10': { title: 'Quiz — Week 10: Future', questions: [
      { q: '"I am going to study" expresses:', opts: ['a past action','a habit','a decided future plan','a present action'], a: 2 },
      { q: 'Correct: She ___ open a business next year.', opts: ['is going to','are going to','am going to','goes to'], a: 0 },
      { q: '"Will" is used for:', opts: ['habits','past events','spontaneous decisions and predictions','directions'], a: 2 },
      { q: 'My dream is ___ a doctor.', opts: ['to become','become','becoming','became'], a: 0 },
      { q: '"In 5 years, I will have my own company." This is:', opts: ['a habit','a prediction/future plan','a past event','a direction'], a: 1 },
    ]},
    'a1-g11': { title: 'Quiz — Week 11: Travel', questions: [
      { q: 'Which do you need to travel internationally?', opts: ['a recipe','a passport','a menu','a schedule'], a: 1 },
      { q: '"Do you have a reservation?" is asked at:', opts: ['the market','the hospital','the hotel reception','the school'], a: 2 },
      { q: '"Check-in" at a hotel means:', opts: ['leaving the hotel','arriving and registering','paying the bill','ordering food'], a: 1 },
      { q: '"Luggage" means:', opts: ['your passport','your bags and suitcases','your ticket','your hotel key'], a: 1 },
      { q: '"I\'d like to stay for 3 nights" means:', opts: ['Leave in 3 days.','Sleep at hotel for 3 days.','I need 3 rooms.','I arrive in 3 days.'], a: 1 },
    ]},
    'a1-g12': { title: 'Quiz — Week 12: Final Review', questions: [
      { q: 'How do you introduce yourself formally?', opts: ["What's up, I'm Kofi.","Hello, my name is Kofi.","Hey! I'm Kofi!","Yo, Kofi here."], a: 1 },
      { q: 'Which shows progress?', opts: ["Before, I couldn't speak. Now I can.","I never study English.","English is very hard.","I don't understand anything."], a: 0 },
      { q: '"I am going to continue studying" uses:', opts: ['past simple','going to future','present simple','present continuous'], a: 1 },
      { q: 'To talk about last weekend:', opts: ['I go to the beach.','I went to the beach.','I am going to the beach.','I will go to the beach.'], a: 1 },
      { q: '"Thank you for listening" is said:', opts: ['at the beginning','in the middle','at the end of a presentation','when asking a question'], a: 2 },
    ]},
    // Conjugating A1
    'a1-c1': { title: 'Quiz — To Be', questions: [
      { q: 'She ___ a teacher.', opts: ['am','is','are','be'], a: 1 },
      { q: 'We ___ students.', opts: ['is','am','are','was'], a: 2 },
      { q: '___ you from Senegal?', opts: ['Am','Is','Are','Be'], a: 2 },
      { q: 'I ___ not tired.', opts: ['is','are','am','be'], a: 2 },
      { q: '___ they your friends?', opts: ['Am','Is','Are','Be'], a: 2 },
    ]},
    'a1-c2': { title: 'Quiz — Present Simple', questions: [
      { q: 'She ___ English every day. (study)', opts: ['study','studies','studying','studied'], a: 1 },
      { q: 'I ___ coffee. (not like)', opts: ["don't like","doesn't like","isn't like","aren't like"], a: 0 },
      { q: '___ he work in Dakar?', opts: ['Do','Does','Is','Are'], a: 1 },
      { q: '"Every morning" signals:', opts: ['Past','Present continuous','Present simple','Future'], a: 2 },
      { q: 'They ___ football on Sundays.', opts: ['plays','play','playing','played'], a: 1 },
    ]},
    'a1-c3': { title: 'Quiz — Have/Has', questions: [
      { q: 'I ___ a new phone.', opts: ['has','have','had','having'], a: 1 },
      { q: 'She ___ two brothers.', opts: ['have','has','had','having'], a: 1 },
      { q: 'They ___ a big house.', opts: ['has','have','had','having'], a: 1 },
      { q: 'He ___ a car? (question)', opts: ['Do he have','Does he have','Has he','Have he'], a: 1 },
      { q: 'The school ___ a big library.', opts: ['have','has','had','having'], a: 1 },
    ]},
    // Reading & Speaking A1 quizzes
    'a1-rs1': { title: 'Quiz — A New Beginning', questions: [
      { q: 'Where does Aminata live?', opts: ['Lagos','Dakar','Abidjan','Conakry'], a: 1 },
      { q: 'Why does Aminata want to learn English?', opts: ['For fun','To travel and find a good job','Her teacher told her to','She has nothing else to do'], a: 1 },
      { q: 'What did the teacher say on the first day?', opts: ['"Good morning class."','"Today is a new beginning."','"English is easy."','"Welcome to Dakar."'], a: 1 },
      { q: 'What did Aminata write in her notebook?', opts: ['"I love Dakar."','"English is fun."','"Today is the first day of my new life."','"My teacher is kind."'], a: 2 },
      { q: 'How did Aminata feel after class?', opts: ['Sad and tired','Full of hope','Angry','Confused'], a: 1 },
    ]},
    'a1-rs2': { title: 'Quiz — The Market Day', questions: [
      { q: 'What time does Oumar wake up?', opts: ['5am','6am','7am','8am'], a: 1 },
      { q: 'What does Oumar need to buy? (not mentioned)', opts: ['tomatoes','fish','bread','mangoes'], a: 2 },
      { q: 'Who does Oumar meet at the fish stall?', opts: ['His brother','His teacher','His friend Ibrahima','His neighbor'], a: 2 },
      { q: 'What does Oumar try at the mango stall?', opts: ['He sings','He speaks English','He bargains in Wolof','He takes a photo'], a: 1 },
      { q: '"English opens many doors" means:', opts: ['English is difficult','English helps you find many opportunities','English teachers are kind','English is expensive to learn'], a: 1 },
    ]},
    'a1-rs3': { title: 'Quiz — The Job Interview', questions: [
      { q: 'What did Mariama study?', opts: ['Medicine','Law','Accounting','Engineering'], a: 2 },
      { q: 'What does Mariama practice before the interview?', opts: ['Cooking','English and interview questions','Dancing','Mathematics'], a: 1 },
      { q: 'What does Mrs. Chen ask first?', opts: ['"Why do you want this job?"','"Tell us about yourself."','"What are your weaknesses?"','"Where are you from?"'], a: 1 },
      { q: 'How does Mariama find out she got the job?', opts: ['By phone call','By letter','By email','Her friend tells her'], a: 2 },
      { q: 'What does Mariama do when she gets the good news?', opts: ['She cries','She calls her mother','She writes a letter','She goes back to work'], a: 1 },
    ]},
    'a1-rs4': { title: 'Quiz — The Wonders of Senegal', questions: [
      { q: 'Where is Senegal located?', opts: ['East Africa','Southern Africa','West Africa','North Africa'], a: 2 },
      { q: 'What is "Teranga"?', opts: ['A type of food','The spirit of hospitality and welcome','A city in Senegal','A traditional dance'], a: 1 },
      { q: 'Why does Lac Rose appear pink?', opts: ['Pollution','A type of algae','The sunset reflection','Pink sand'], a: 1 },
      { q: 'What is the most popular sport in Senegal?', opts: ['Football','Basketball','Wrestling','Swimming'], a: 2 },
      { q: 'What is Gorée Island known for?', opts: ['Its beaches','Its markets','Its history related to the slave trade','Its food'], a: 2 },
    ]},
    'a1-rs5': { title: 'Quiz — Two Friends, One Dream', questions: [
      { q: 'How long have Kofi and Seun been friends?', opts: ['Since they were 5','Since they were 7','Since university','Since last year'], a: 1 },
      { q: 'What is their business idea?', opts: ['A restaurant','A football club','Teaching digital skills to young people','A fashion company'], a: 2 },
      { q: 'Why do they study English?', opts: ['Their teacher told them to','Most international business is done in English','They want to travel to England','English is easy'], a: 1 },
      { q: 'What does Adaeze say at the end of the first day?', opts: ['"This is boring."','"I already knew all of this."','"This is the first time someone believed in me."','"When is the next class?"'], a: 2 },
      { q: 'What does Kofi write in his journal?', opts: ['"We started today. We will not stop."','"Today was difficult."','"I miss home."','"We need more money."'], a: 0 },
    ]},
    'a1-rs6': { title: 'Quiz — Climate Change and Africa', questions: [
      { q: 'How much of global emissions is Africa responsible for?', opts: ['Less than 4%','About 20%','More than 30%','About 10%'], a: 0 },
      { q: 'What is desertification?', opts: ['Building new cities','The desert expanding and destroying farmland','Planting new forests','Cleaning rivers'], a: 1 },
      { q: 'Which country is mentioned as having coastline at risk?', opts: ['Mali','Nigeria','Senegal','Ethiopia'], a: 2 },
      { q: 'What are young Africans doing about climate change?', opts: ['Nothing','Finding creative solutions and speaking at conferences','Leaving the continent','Blaming other countries'], a: 1 },
      { q: 'The text says Africa is:', opts: ['Only a victim','Only causing the problem','Both affected and finding solutions','Not affected at all'], a: 2 },
    ]},
    'a1-rs7': { title: 'Quiz — A Letter Home', questions: [
      { q: 'Where is Ndéye studying?', opts: ['Paris','New York','London','Toronto'], a: 1 },
      { q: 'What did Mrs. Patterson say about mistakes?', opts: ['"Never make mistakes."','"Mistakes are steps forward."','"Mistakes are failures."','"Speak only when perfect."'], a: 1 },
      { q: 'What food does Ndéye miss most?', opts: ['Jollof rice','Thiéboudienne','French bread','Soup'], a: 1 },
      { q: 'How is the weather in England?', opts: ['Very hot and sunny','Very cold with snow','It rains almost every day','Perfect weather'], a: 2 },
      { q: 'When will Ndéye return home?', opts: ['In June','In September','In December','In March'], a: 2 },
    ]},
    'a1-rs8': { title: 'Quiz — The Power of Education', questions: [
      { q: 'Who faces the most obstacles in education?', opts: ['Boys in cities','Girls in rural areas','University students','Adult learners'], a: 1 },
      { q: 'What is Vanessa Nakate known for?', opts: ['Being a doctor','Environmental and education justice activism','Writing books','Teaching English'], a: 1 },
      { q: 'What does a good teacher do?', opts: ['Only teach the subject','Inspire students to dream bigger and believe in themselves','Give many tests','Make school very strict'], a: 1 },
      { q: 'Why is learning English important according to the text?', opts: ['It is easy','It connects students to global knowledge and opportunity','It is required by law','All teachers speak English'], a: 1 },
      { q: 'Education is described as:', opts: ['A privilege for the rich','A right for everyone','Too expensive','Only for young people'], a: 1 },
    ]},
    'a1-rs9': { title: 'Quiz — The Old Fisherman', questions: [
      { q: 'How long has Modu been a fisherman?', opts: ['Twenty years','Thirty years','Forty years','Fifty years'], a: 3 },
      { q: 'Who comes to visit Modu?', opts: ['His son','A journalist','A young man named Cheikh','His old friend'], a: 2 },
      { q: 'What does Cheikh ask Modu?', opts: ['How to fish','How to honor the past without being trapped by it','Where to find a job','How to speak English'], a: 1 },
      { q: 'What does Modu compare to the ocean?', opts: ['Money','Time','Life — always moving but remembering its roots','Work'], a: 2 },
      { q: 'How does Cheikh feel at the end?', opts: ['Angry','Sad','Completely at peace','Confused'], a: 2 },
    ]},
    'a1-rs10': { title: 'Quiz — Technology in Africa', questions: [
      { q: 'What is M-Pesa?', opts: ['A music app','A mobile money platform','A type of food','A football team'], a: 1 },
      { q: 'Which city is called "Africa\'s Silicon Valley"?', opts: ['Nairobi','Cairo','Lagos','Cape Town'], a: 2 },
      { q: 'What advantage does Africa have according to the text?', opts: ['Old infrastructure','The youngest population in the world','The most universities','The most investors'], a: 1 },
      { q: 'What challenge remains for tech in Africa?', opts: ['Too many smartphones','Unequal access to electricity and internet','Too many startups','Not enough young people'], a: 1 },
      { q: 'Wave is mentioned as an example of:', opts: ['A social media platform','A news website','A mobile money platform','A music streaming app'], a: 2 },
    ]},
    'a1-rs11': { title: 'Quiz — The Doctor\'s Choice', questions: [
      { q: 'Where was Dr. Fatimata offered a job in Europe?', opts: ['London','Berlin','Paris','Rome'], a: 2 },
      { q: 'Why did Fatimata decide to become a doctor?', opts: ['Her parents told her to','She wanted to be rich','Her brother was saved by a kind doctor','She loved science'], a: 2 },
      { q: 'What does Fatimata choose?', opts: ['The Paris hospital','The rural clinic in northern Senegal','To take a break','To continue studying'], a: 1 },
      { q: 'How many patients does she treat on her first day?', opts: ['12','25','47','100'], a: 2 },
      { q: 'Why does Fatimata cry at the end?', opts: ['From sadness','From tiredness','From the deepest sense of purpose','From loneliness'], a: 2 },
    ]},
    'a1-rs12': { title: 'Quiz — The World in One Classroom', questions: [
      { q: 'How many students were in the class?', opts: ['8','10','12','15'], a: 2 },
      { q: 'What does Ibrahim say he learned?', opts: ['English grammar','That fear is just a feeling, not the truth','How to write emails','How to travel'], a: 1 },
      { q: 'What does Aissatou say English is?', opts: ['A difficult language','A waste of time','A bridge that connects people','Only for business'], a: 2 },
      { q: 'What does Moussa say is the most important thing?', opts: ['To be perfect','To study hard','To try','To speak fast'], a: 2 },
      { q: 'What did the classroom become at the end?', opts: ['A library','A community','A business','A school'], a: 1 },
    ]},
    // A2
    'a2-g1': { title: 'Quiz — Comparatifs', questions: [
      { q: 'She is ___ student in the class. (good)', opts: ['gooder','more good','the best','the goodest'], a: 2 },
      { q: 'Dakar is ___ Saint-Louis. (big)', opts: ['more big than','bigger than','the biggest','big than'], a: 1 },
      { q: 'English is ___ ever. (important)', opts: ['more important than','importanter','most important','the importantest'], a: 0 },
      { q: 'Superlatif de "tall":', opts: ['taller','tallest','the tallest','more tall'], a: 2 },
    ]},
    'a2-g2': { title: 'Quiz — Prépositions', questions: [
      { q: 'I wake up ___ 6am.', opts: ['in','on','at','by'], a: 2 },
      { q: 'The book is ___ the table.', opts: ['in','on','at','by'], a: 1 },
      { q: 'She was born ___ June.', opts: ['at','on','in','by'], a: 2 },
      { q: 'I study ___ school.', opts: ['in','on','at','by'], a: 2 },
    ]},
    'a2-c1': { title: 'Quiz — Présent vs Continu', questions: [
      { q: 'She ___ right now. (work)', opts: ['work','works','is working','worked'], a: 2 },
      { q: '"Usually" signals:', opts: ['past','continuous','present simple','future'], a: 2 },
      { q: '"At the moment" signals:', opts: ['past simple','present simple','present continuous','future'], a: 2 },
      { q: 'I ___ coffee every morning. (drink)', opts: ['am drinking','drink','drinks','drinking'], a: 1 },
    ]},
    'a2-c2': { title: 'Quiz — Passé Simple', questions: [
      { q: 'Past of "go":', opts: ['goed','gone','went','goes'], a: 2 },
      { q: 'Past of "have":', opts: ['haved','had','has','having'], a: 1 },
      { q: '"Last week" signals:', opts: ['present simple','present continuous','past simple','future'], a: 2 },
      { q: 'I ___ a great film yesterday.', opts: ['watch','watches','watching','watched'], a: 3 },
    ]},
    'a2-rs1': { title: 'Quiz — Email professionnel', questions: [
      { q: 'A formal email starts with:', opts: ["Hey!","Dear Mr./Ms.","Hello friend","What's up"], a: 1 },
      { q: '"I am writing to register" is used:', opts: ['at the end','in the middle','at the beginning to state purpose','in the greeting'], a: 2 },
      { q: '"Best regards" is used:', opts: ['to start an email','to end an email formally','to ask a question','to give information'], a: 1 },
    ]},
    'a2-rs2': { title: 'Quiz — Daily routine', questions: [
      { q: '"Wake up" means:', opts: ['go to sleep','stop sleeping','start dreaming','eat breakfast'], a: 1 },
      { q: '"Have dinner" means:', opts: ['breakfast','lunch','evening meal','snack'], a: 2 },
      { q: '"Go to bed" means:', opts: ['wake up','start working','go to sleep','come home'], a: 2 },
    ]},
  },
  modules: {
    'Grammar': { title: 'Quiz Module — Grammar A1', questions: [
      { q: 'She ___ a doctor.', opts: ['am','is','are','be'], a: 1 },
      { q: '"I" is always written:', opts: ['in lowercase','in uppercase','optional','in italic'], a: 1 },
      { q: 'I ___ a car. (have)', opts: ['has','have','having','had'], a: 1 },
      { q: 'He ___ to school every day. (go)', opts: ['go','goes','going','went'], a: 1 },
      { q: '"Every morning" signals:', opts: ['Past','Continuous','Present simple','Future'], a: 2 },
      { q: 'Past of "go":', opts: ['goed','gone','went','goes'], a: 2 },
      { q: '"Would you like to come?" is:', opts: ['a refusal','an invitation','a direction','a complaint'], a: 1 },
      { q: 'Your mother\'s brother is your:', opts: ['cousin','uncle','grandfather','nephew'], a: 1 },
    ]},
    'Conjugating': { title: 'Quiz Module — Conjugating A1', questions: [
      { q: 'She ___ right now. (work)', opts: ['work','works','is working','worked'], a: 2 },
      { q: 'Past of "have":', opts: ['haved','had','has','having'], a: 1 },
      { q: 'I ___ coffee. (not like)', opts: ["don't like","doesn't like","didn't like","wasn't like"], a: 0 },
      { q: 'They ___ football every Sunday.', opts: ['plays','play','playing','played'], a: 1 },
      { q: 'She ___ homework yesterday. (finish)', opts: ['finish','finishes','finishing','finished'], a: 3 },
    ]},
    'Reading & Speaking': { title: 'Quiz Module — Reading & Speaking A1', questions: [
      { q: 'Where does Aminata live?', opts: ['Lagos','Dakar','Abidjan','Conakry'], a: 1 },
      { q: '"Teranga" means:', opts: ['A food','Hospitality and welcome','A city','A dance'], a: 1 },
      { q: 'What does Modu compare to the ocean?', opts: ['Money','Time','Life — always moving but remembering roots','Work'], a: 2 },
      { q: 'Mariama got the job by:', opts: ['phone call','letter','email','her friend told her'], a: 2 },
      { q: '"Mistakes are steps forward" was said by:', opts: ['Aminata','Mrs. Patterson','Modu','Fatimata'], a: 1 },
      { q: 'How many patients did Fatimata treat on day 1?', opts: ['12','25','47','100'], a: 2 },
    ]},
  },
  final: {
    A1: { title: 'Examen Final — Niveau A1', questions: [] },
  },
};

// Génère les 120 questions de l'examen final
(function buildFinalQuiz() {
  const all = [];
  Object.values(QUIZ_DATA.courses).forEach(q => all.push(...q.questions));
  Object.values(QUIZ_DATA.modules).forEach(q => all.push(...q.questions));
  while (all.length < 120) {
    Object.values(QUIZ_DATA.courses).forEach(q => all.push(...q.questions));
  }
  QUIZ_DATA.final.A1.questions = all.slice(0, 120);
})();